import React, { useEffect, useMemo, useRef } from 'react';
import styles from '../../styles/scss/components/tips/influencerModal.module.scss';

const InfluencerModal = ({ isOpen, onClose, influencer }) => {
  // Reference to check if script has been loaded
  const vimeoScriptLoaded = useRef(false);
  
  // Load Vimeo player script when modal opens with a Vimeo video
  useEffect(() => {
    if (isOpen && influencer && influencer.details && 
        influencer.details.video && 
        influencer.details.video.provider === 'vimeo' && 
        !vimeoScriptLoaded.current) {
      
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      document.body.appendChild(script);
      
      vimeoScriptLoaded.current = true;
      
      return () => {
        // Clean up is optional as the script will remain in the DOM
      };
    }
  }, [isOpen, influencer]);
  const socialMediaLinks = useMemo(() => {
    if (!influencer) return [];
    
    if (influencer.details && influencer.details.socialMedia && influencer.details.socialMedia.length > 0) {
      return influencer.details.socialMedia;
    }
    
    const links = [];
    
    if (influencer.instagram) {
      links.push({ platform: 'instagram', url: `https://www.instagram.com/${influencer.instagram}` });
    }
    
    if (influencer.xUsername) {
      links.push({ platform: 'twitter', url: `https://twitter.com/${influencer.xUsername}` });
    }
    
    if (influencer.youtube) {
      links.push({ platform: 'youtube', url: `https://www.youtube.com/${influencer.youtube}` });
    }
    
    if (influencer.facebook) {
      links.push({ platform: 'facebook', url: `https://www.facebook.com/${influencer.facebook}` });
    }
    
    if (influencer.tiktok) {
      links.push({ platform: 'tiktok', url: `https://www.tiktok.com/@${influencer.tiktok}` });
    }
    
    return links;
  }, [influencer]);
  // Add event listener for ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore scrolling when modal is closed
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {/* Header Section with Background Image */}
        {influencer && (
          <>
            <div 
              className={styles.headerSection}
              style={{ backgroundImage: `url(${influencer.details && influencer.details.headerImage ? influencer.details.headerImage : influencer.url})` }}
            >
              <div className={styles.headerContent}>
                <h1 className={styles.influencerName}>MEET {influencer.title}</h1>
                <div className={styles.category}>{influencer.category}</div>
              </div>
            </div>
            
            {/* Two-column Content Section */}
            <div className={styles.contentWrapper}>
              <div className={styles.twoColumnLayout}>
                {/* Left Column - Profile Image */}
                <div className={styles.leftColumn}>
                  <div className={styles.profileImageContainer}>
                    <img 
                      src={influencer.details && influencer.details.profileImage ? influencer.details.profileImage : influencer.url} 
                      alt={influencer.alt || influencer.title} 
                      className={styles.profileImage} 
                    />
                  </div>
                </div>
                
                {/* Right Column - Content */}
                <div className={styles.rightColumn}>
                  <div className={styles.profileContent}>
                    <p className={styles.introText}>{influencer.details && influencer.details.introText ? influencer.details.introText : ""}</p>
                    
                    {/* Social Media Links */}
                    {socialMediaLinks.length > 0 && (
                      <div className={styles.socialSection}>
                        <h3 className={styles.socialTitle}>CONNECT</h3>
                        <div className={styles.socialLinks}>
                          {socialMediaLinks.map((social, index) => (
                            <a 
                              key={index}
                              href={social.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.socialLink}
                              aria-label={social.platform}
                            >
                              <div className={styles.socialIcon}>
                                {social.platform === 'instagram' && (
                                  <svg viewBox="0 0 448 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="currentColor" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                                  </svg>
                                )}
                                {social.platform === 'twitter' && (
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24">
                                    <path fill="currentColor" d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
                                  </svg>
                                )}
                                {social.platform === 'youtube' && (
                                  <svg viewBox="0 0 576 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="currentColor" d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"/>
                                  </svg>
                                )}
                                {social.platform === 'facebook' && (
                                  <svg viewBox="0 0 320 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="currentColor" d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
                                  </svg>
                                )}
                                {social.platform === 'tiktok' && (
                                  <svg viewBox="0 0 448 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="currentColor" d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                                  </svg>
                                )}
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Video Section - Full Width */}
            {influencer.details && influencer.details.video && (
              <div className={styles.videoSection}>
                <div className={styles.videoContainer}>
                  {influencer.details.video.provider === 'vimeo' ? (
                    <div style={{padding:'56.25% 0 0 0', position:'relative'}}>
                      <iframe 
                        src={influencer.details.video.embedUrl}
                        title={influencer.details.video.title || (influencer.title + ' video')}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : (
                    <video
                      src={influencer.details.video.videoUrl}
                      title={influencer.details.video.title || `${influencer.title} video`}
                      controls
                      playsInline
                      className={styles.videoIframe}
                    ></video>
                  )}
                </div>
              </div>
            )}
            
            {/* Products Gallery Section */}
            {influencer.details && influencer.details.favoriteProducts && influencer.details.favoriteProducts.length > 0 && (
              <div className={styles.productsSection}>
                <div className={styles.productsContainer}>
                  <h2 className={styles.productsTitle}>
                    {influencer.title}'S FAVORITE PRODUCTS
                  </h2>
                  <div className={styles.productsGrid}>
                    {influencer.details.favoriteProducts.map((product, index) => (
                      <div key={product.id || index} className={styles.productCard}>
                        <div className={styles.productImageContainer}>
                          <img 
                            src={product.image || `/img/tips-page/silicone.png`} 
                            alt={product.name || 'WD-40 Product'} 
                            className={styles.productImage} 
                          />
                        </div>
                        
                        <a 
                          href={product.whereToBuy} 
                          className={styles.productButton}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          WHERE TO BUY
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InfluencerModal;
